import React, { useEffect, useState } from 'react';
import SmileFaceNeutral from '../../assets/Smile_Face_Neutral.svg';
import SmileFaceTop from '../../assets/Smile_Face_Top.svg';
import SmileFaceMedium from '../../assets/Smile_Face_Medium.svg';
import SmileFaceBottom from '../../assets/Smile_Face_Bottom.svg';
import { StackRankBandColors } from '../../utils/stackRankColors';

type MetricSliderProps = {
  metrics: { id: number; name: string; type: string; content: string; agentName: string, smiley: string }[];
  onMetricChange: (metricId: number, metricType: string) => void;
  // P4P multi-tier (E20-6317). When true, the arrow/nav accent color follows the metric
  // currently shown (StackRankBandColors[currentIndex].meets - same "meets" identity shade used
  // for the grid/ranking-card selection elsewhere) instead of the fixed legacy teal. Computed
  // from this component's own currentIndex rather than a color passed down from the parent, so
  // there's no risk of it lagging behind whichever metric name is actually on screen.
  isTiered?: boolean;
};

const MetricSlider: React.FC<MetricSliderProps> = ({ metrics, onMetricChange, isTiered }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [metricStackRankHeader, setMetricStackRankHeader] = useState('');

  const smileyMap: { [key: string]: string } = {
    neutral: SmileFaceNeutral,
    top: SmileFaceTop,
    medium: SmileFaceMedium,
    bottom: SmileFaceBottom
  };

  const accentColor = isTiered
    ? (StackRankBandColors[currentIndex % StackRankBandColors.length] ?? StackRankBandColors[0]).meets
    : '#00B6B2';
  const handleNext = () => {
    if (!metrics.length) return;
    const nextIndex = (currentIndex + 1) % metrics.length;
    setCurrentIndex(nextIndex);
    onMetricChange(metrics[nextIndex].id, metrics[nextIndex].type);
  };

  const handlePrev = () => {
    if (!metrics.length) return;
    const prevIndex = (currentIndex - 1 + metrics.length) % metrics.length;
    setCurrentIndex(prevIndex);
    onMetricChange(metrics[prevIndex].id, metrics[prevIndex].type);
  };

  useEffect(() => {
    if (!metrics.length) return;
    onMetricChange(metrics[currentIndex]?.id, metrics[currentIndex]?.type);
    if (metrics[currentIndex]?.agentName === '')
      setMetricStackRankHeader('What is Stack Ranking?');
    else if (metrics[currentIndex]?.agentName === 'I') {
      setMetricStackRankHeader('Where Do I Stand?');
    }
    else {
      setMetricStackRankHeader(`Where Does ${metrics[currentIndex]?.agentName || ' '} Stand?`);
    }
  }, [currentIndex, metrics, onMetricChange]);

  if (!metrics.length) return null;
  return (
    <div className="p-4 rounded-lg w-full flex flex-col items-center">

      <div className="flex items-center justify-center gap-4 mt-2" style={{ color: accentColor }}>
        <button
          onClick={handlePrev}
          className="nav-buttons cursor-pointer text-blue-500 px-2"
          aria-label="Previous Metric">
          <i className="pi pi-angle-left" style={{ color: accentColor, fontSize: '25px' }} />
        </button>
        <span style={{ fontSize: '25px', verticalAlign: 'bottom' }} className="mx-4">{metrics[currentIndex]?.name}</span>
        <button
          onClick={handleNext}
          className="nav-buttons cursor-pointer text-blue-500 px-2"
          aria-label="Next Metric">
          <i className="pi pi-angle-right" style={{ color: accentColor, fontSize: '25px' }} />
        </button>
      </div>
      {metrics[currentIndex]?.content !== '' && (<div style={{display: 'flex',alignItems: 'flex-start',gap: '24px',marginTop: '50px',flexWrap: 'wrap'}}>
        <div style={{ flexShrink: 0 }}>
          <img src={smileyMap[metrics[currentIndex]?.smiley]} alt="face" style={{ width: '150px', height: '150px', objectFit: 'contain' }}/>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
          <h4 className="title">{metricStackRankHeader}</h4>
          <div className="message" style={{ fontSize: '20px',fontWeight:'100' }} dangerouslySetInnerHTML={{ __html: metrics[currentIndex]?.content }}/>
        </div>
      </div>)}

    </div>
  );
};

export default MetricSlider;